<script setup lang="ts">
import { PiniaColadaDevtools } from "@pinia/colada-devtools";
import useDeviceSocket from "~/composables/use-socket-io/use-device-socket";
import useEventSocket from "~/composables/use-socket-io/use-event-socket";
import useUserSocket from "~/composables/use-socket-io/use-user-socket";
import { initAppleSdk } from "~/services/auth/apple";
import { initFacebookSdk } from "~/services/auth/facebook";
import { initGoogleSdk } from "~/services/auth/google";
import DeviceAPI from "~/services/device";
import EventsAPI from "~/services/events";
import FollowAPI from "~/services/follow";
import useUserStore from "~/stores/user";
import getErrorLocalePathMessage from "~/utils/locale";
import useSocketIO from "./composables/use-socket-io";
import { THEME_COLOR } from "./constants";

const route = useRoute();
const router = useRouter();
const toaster = useToast();
const { t, te } = useI18n();
const socket = useSocketIO();
const color = useColorMode();
const appConfig = useRuntimeConfig().public;
const userStore = useUserStore();
const { locale: currentLocale } = useI18n();
const storeUrl = route.query.storeUrl as string;
const isLeagueView = appConfig.APP_CUSTOMER.enable_leagues_view;

function onStoreRedirect() {
  if (!storeUrl || storeUrl === "undefined")
    return;

  window.location.href = storeUrl;
}

function onDeviceLimitError(error?: Error) {
  if ((error as Error & { code?: string })?.code === "refresh_token_failed")
    return;

  userStore.logout();
  router.push({ path: "/auth/login/", query: { deviceNoLongerAllowedError: "true" } });
}

onMounted(() => {
  onStoreRedirect();

  if (appConfig.APP_CUSTOMER.app_config.enable_auth_via_facebook && appConfig.APP_CUSTOMER.app_config.app_id_facebook)
    initFacebookSdk();

  if (appConfig.APP_CUSTOMER.app_config.enable_auth_via_google && appConfig.APP_CUSTOMER.app_config.app_client_id_for_google)
    initGoogleSdk();

  if (appConfig.APP_CUSTOMER.app_config.enable_auth_via_apple && appConfig.APP_CUSTOMER.app_config.app_client_id_for_apple)
    initAppleSdk();

  if (!appConfig.APP_CUSTOMER.enable_white_theme && color?.preference)
    color.preference = "dark";

  if (socket) {
    useDeviceSocket(socket, { onDeviceRemoved: onDeviceLimitError });
    useUserSocket(socket);
    useEventSocket(socket);
  }

  if (userStore.isAuthenticated) {
    DeviceAPI.checkCurrent().catch((error: Error | undefined) => onDeviceLimitError(error));

    Promise.all([
      EventsAPI.selected.load(),
      isLeagueView ? FollowAPI.load({ types: "team" }) : FollowAPI.load({ types: "identity_item" }),
    ]).catch((error: Error) => {
      const { message } = getErrorLocalePathMessage(error.message, { t, te });
      toaster.error(message);
    });
  }
});

useHead({
  meta: [
    { name: "theme-color", content: computed(() => (color.value === THEME_COLOR.DARK ? "#0f1624" : "#f4f7fc")) },
  ],
  // @ts-expect-error FIXME:
  htmlAttrs: [{
    lang: computed(() => currentLocale.value || "en"),
    dir: computed(() => currentLocale.value === "he" ? "rtl" : "ltr"),
  }],
});
</script>

<template>
  <UModals />
  <SmartAppBanner />
  <UserSurvey />

  <PiniaColadaDevtools />

  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
</template>
